import { Directive, ElementRef, HostBinding, Input, OnChanges, SimpleChanges } from '@angular/core';

const SHRINK_TO_TARGET_SELECTOR = '.ui-shrink-to';
@Directive({
  standalone: false,
  selector: '[campusShrinkToTarget]',
})
export class ShrinkToTargetDirective {
  @HostBinding(`class${SHRINK_TO_TARGET_SELECTOR}`) hasShrinkToTargetClass = true;
}

@Directive({
  standalone: false,
  selector: '[campusShrinkToTrigger]',
})
export class ShrinkToDirective implements OnChanges {
  @HostBinding('class.ui-shrink-to-trigger') hasShrinkToTriggerClass = true;

  @Input() campusShrinkToTrigger: boolean;
  @Input() shrinkClass: string;

  constructor(private element: ElementRef) {}

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.campusShrinkToTrigger?.currentValue && !changes.campusShrinkToTrigger.isFirstChange()) {
      if (!document.documentElement.animate) return;

      const fromElement = this.element.nativeElement as HTMLElement;
      const toElement = await this.getTarget();

      const pseudoElement = this.getPseudoElement(fromElement);

      let observer: MutationObserver;
      if (this.shrinkClass) {
        pseudoElement.classList.add(...this.shrinkClass.trim().split(' '));
      } else {
        observer = this.syncCssClasses(pseudoElement, fromElement);
      }

      document.body.appendChild(pseudoElement);

      const animation = this.animate(pseudoElement, fromElement, toElement);

      animation.onfinish = () => {
        document.body.removeChild(pseudoElement);
        if (observer) {
          observer.disconnect();
        }
      };
    }
  }

  private async getTarget(): Promise<HTMLElement> {
    return new Promise((resolve) => {
      const selectElement = () => document.querySelector(SHRINK_TO_TARGET_SELECTOR) as HTMLElement;

      if (selectElement()) {
        return resolve(selectElement());
      }

      const observer = new MutationObserver(() => {
        if (selectElement()) {
          resolve(selectElement());
          observer.disconnect();
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  private getPseudoElement(fromElement: HTMLElement) {
    const from = fromElement.getBoundingClientRect();
    const pseudoEl = document.createElement('div');

    pseudoEl.style.position = 'fixed';
    pseudoEl.style.left = `${from.x}px`;
    pseudoEl.style.top = `${from.y}px`;
    pseudoEl.style.width = `${from.width}px`;
    pseudoEl.style.height = `${from.height}px`;
    pseudoEl.style.zIndex = '99999';

    return pseudoEl;
  }

  private syncCssClasses(pseudoElement: HTMLDivElement, fromElement: HTMLElement) {
    pseudoElement.classList.add(...Array.from(fromElement.classList));

    //Added an observer to pick up the selected classes of a list-item after this change was triggered
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          pseudoElement.classList.remove(...Array.from(pseudoElement.classList));
          pseudoElement.classList.add(...Array.from((mutation.target as HTMLElement).classList));
        }
      }
    });
    observer.observe(fromElement, { attributes: true });

    return observer;
  }

  private animate(pseudoElement: HTMLDivElement, fromElement: HTMLElement, toElement: HTMLElement) {
    const to = toElement.getBoundingClientRect();
    const from = fromElement.getBoundingClientRect();

    const transformTo = {
      transform: `translate(${to.x - from.x}px, ${to.y - from.y}px)`,
      width: `${to.width}px`,
      height: `${to.height}px`,
      opacity: 'var(--opacity-low)',
    };

    const animation = pseudoElement.animate(transformTo, { duration: 500, easing: 'ease-in' });
    toElement.animate([{ borderColor: 'var(--primary-color)' }, { borderColor: 'transparent' }], {
      duration: 300,
      delay: 450,
    });

    return animation;
  }
}
